import * as THREE from 'three';
import type { Brand } from '../brand/types';
import type { Tunables } from '../sim/tunables';
import { createCamera } from './camera';
import { createField, type FieldView } from './field';
import { createStadium, type StadiumView } from './stadium';
import { QualityGovernor } from './quality';

export interface SceneStats {
  fps: number;
  drawCalls: number;
  pixelRatio: number;
}

export interface Stage {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  field: FieldView;
  stadium: StadiumView;
  stats: SceneStats;
  /** Current canvas size in CSS px. */
  size: { width: number; height: number };
  start(onFrame: (dt: number) => void): void;
  dispose(): void;
}

/** Renderer, lights, field, camera, resize and the frame loop with resolution scaling. */
export function createStage(container: HTMLElement, brand: Brand, logo: HTMLImageElement | null, t: Tunables): Stage {
  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  const quality = new QualityGovernor(t, window.devicePixelRatio || 1);
  renderer.setPixelRatio(quality.pixelRatio);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = t.look.toneExposure;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(t.look.sky);
  scene.fog = new THREE.Fog(t.look.fog, t.look.fogNear, t.look.fogFar);
  scene.add(new THREE.HemisphereLight(t.look.hemisphereSky, t.look.hemisphereGround, t.look.hemisphereIntensity));
  const key = new THREE.DirectionalLight(t.look.keyLight, t.look.keyLightIntensity);
  key.position.fromArray(t.look.keyLightPosition);
  scene.add(key);
  const rim = new THREE.DirectionalLight(t.look.rimLight, t.look.rimLightIntensity);
  rim.position.fromArray(t.look.rimLightPosition);
  scene.add(rim);

  const field = createField(brand, t, logo, renderer.capabilities.getMaxAnisotropy());
  scene.add(field.group);
  const stadium = createStadium(brand, t, logo, renderer.capabilities.getMaxAnisotropy());
  scene.add(stadium.group);

  const camera = createCamera(t, 1);
  const size = { width: 1, height: 1 };
  const resize = () => {
    size.width = container.clientWidth;
    size.height = container.clientHeight;
    renderer.setSize(size.width, size.height, false);
    camera.aspect = size.width / Math.max(1, size.height);
    camera.updateProjectionMatrix();
  };
  const observer = new ResizeObserver(resize);
  observer.observe(container);
  resize();

  const stats: SceneStats = { fps: 0, drawCalls: 0, pixelRatio: quality.pixelRatio };
  const timer = new THREE.Timer();

  return {
    renderer,
    scene,
    camera,
    field,
    stadium,
    stats,
    size,
    start(onFrame) {
      renderer.setAnimationLoop((time) => {
        timer.update(time);
        const dt = Math.min(timer.getDelta(), t.render.maxFrameSeconds);
        if (quality.tick(timer.getDelta())) {
          renderer.setPixelRatio(quality.pixelRatio);
          resize();
        }
        onFrame(dt);
        renderer.render(scene, camera);
        stats.fps = quality.fps;
        stats.drawCalls = renderer.info.render.calls;
        stats.pixelRatio = quality.pixelRatio;
      });
    },
    dispose() {
      renderer.setAnimationLoop(null);
      observer.disconnect();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
